/* External depencies */
import React, { useMemo } from 'react';
import get from 'lodash/get';

/* Internal depencies */
import { randomItem } from '../common/helpers/random';
import { cleanObject } from 'src/common/cleanObject';
import { ProfileType } from 'src/types/Profile';

interface ProfileDefaultAvatarProps {
  profile: ProfileType;
  size?: number;
  backgroundColor?: string;
  style?: Object;
}

// Function to generate a random pleasant color
const getRandomColor = () => {
  const colors = ['#FFB300', '#6ACCBC', '#F3D24D', '#6BAED6', '#C594C5', '#E55D4A', '#FC814A'];
  return randomItem(colors);
};

// Persistent color map for profiles
const profileColorMap = new Map<string, string>();

const ProfileDefaultAvatar: React.FC<ProfileDefaultAvatarProps> = ({ profile, size = 50, backgroundColor, style }) => {
  const profileId = get(profile, 'id');
  const profileType = get(profile, 'type');

  // Use memoized stable color based on profile.id
  const stableBackgroundColor = useMemo(() => {
    if (backgroundColor) return backgroundColor; // Use provided color if available

    // Check if a color already exists for this profile
    if (!profileColorMap.has(profileId)) {
      profileColorMap.set(profileId, getRandomColor());
    }

    // Return the persistent color
    return profileColorMap.get(profile.id)!;
  }, [profileId, backgroundColor]);

  const firstName = get(profile, 'firstName', get(profile, 'name', ''));
  const lastName = get(profile, 'lastName', '');
  const initials = `${get(firstName, '[0]', '')}${get(lastName, '[0]', '')}`.toUpperCase();

  const styles = {
    backgroundColor: stableBackgroundColor,
    borderRadius: Boolean(profileType === 'user' || profileType === 'artist') ? 1000 : 5,
    width: size,
    height: size,
    profileSelect: 'none' as const,
    objectFit: 'cover',
    aspectRatio: '1',
    ...style,
  };

  return (
    <div className="d-flex justify-content-center align-items-center" style={styles as any}>
      <h5 className="text-bold m-0 p-0" style={cleanObject({ fontSize: Boolean(typeof size === 'number') ? size / 2.5 : undefined })}>
        {initials}
      </h5>
    </div>
  );
};

export default ProfileDefaultAvatar;
